import { useEffect, useId, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { Navigate, useNavigate } from "react-router-dom";
import { t } from "ttag";

import { PasswordTips } from "@/app/components/legacy-components";
import { ROUTES } from "@/app/router";
import { trackCreatePasswordViewed } from "@/features/analytics/trackers/create-password-viewed";
import { useHandleLogin } from "@/features/auth/hooks/use-handle-login";
import { setPassword } from "@/features/onboarding/api";
import { SuccessAlert } from "@/features/onboarding/components";
import { CallCenterPaymentSuccessAlert } from "@/features/onboarding/components/account/call-center-payment-success-alert";
import { useOnboardingUser } from "@/features/onboarding/hooks";
import { ONBOARDING_ROUTE } from "@/features/onboarding/router";
import { useOnboardingStore } from "@/features/onboarding/stores";
import { LogoLoader } from "@/shared/components";
import { useBoolean } from "@/shared/hooks";
import { confirmPasswordSchema } from "@/shared/models";
import { Form, Layout, PasswordInput, SectionHeader } from "@/shared/ui";
import { handleAxiosFieldErrors } from "@/shared/utils";

export const SetPassword = () => {
  const formId = useId();

  const userToken = useOnboardingStore((state) => state.token);

  const user = useOnboardingUser();

  const reset = useOnboardingStore((state) => state.reset);

  const isCallCenter = useOnboardingStore((state) => state.isCallCenter);

  const [showCallCenterAlert] = useState(
    isCallCenter && Boolean(user) && Boolean(userToken),
  );

  const { value: showSuccessAlert, setTrue: openSuccessAlert } =
    useBoolean(false);

  const navigate = useNavigate();

  const form = useForm({
    resolver: zodResolver(confirmPasswordSchema),
  });

  const { handleLogin } = useHandleLogin(user?.email);

  const setPasswordMutation = useMutation({
    mutationFn: setPassword,
    onSuccess: () => {
      openSuccessAlert();
    },
    onError: (error) => {
      handleAxiosFieldErrors(error, form.setError);
    },
  });

  const handleSuccessAlertContinue = () => {
    if (setPasswordMutation.data) {
      handleLogin(setPasswordMutation.data);
      reset();
      void navigate(ROUTES.HOME);
    }
  };

  useEffect(() => {
    if (showCallCenterAlert || !user) {
      return;
    }

    trackCreatePasswordViewed({ user });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!showCallCenterAlert) {
      return;
    }

    reset();
  }, [showCallCenterAlert, reset]);

  useEffect(() => {
    if (!showSuccessAlert) {
      return;
    }

    const timer = setTimeout(handleSuccessAlertContinue, 5000);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSuccessAlert]);

  if (showCallCenterAlert) {
    return <CallCenterPaymentSuccessAlert />;
  }

  if (!user || !userToken) {
    return <Navigate to={ONBOARDING_ROUTE.MAIN_INFO} />;
  }

  const handleSubmit = form.handleSubmit((data) => {
    setPasswordMutation.mutate({ ...data, token: userToken });
  });

  const email = user.email;

  return (
    <>
      <LogoLoader show={setPasswordMutation.isPending} fullscreen />

      {setPasswordMutation.isSuccess && (
        <SuccessAlert
          open={showSuccessAlert}
          onContinue={handleSuccessAlertContinue}
        />
      )}

      <Layout.Content>
        <div className="mf-masked mf-listen">
          <SectionHeader
            title={t`Payment successful! Now create your password`}
            description={t`You are creating an account for the email ${email}`}
          />
        </div>

        <Form.Provider {...form}>
          <section className="flex w-full grow flex-col gap-1.5">
            <form
              id={formId}
              onSubmit={handleSubmit}
              className="grid content-start gap-x-8 gap-y-1.5 sm:grid-cols-2"
            >
              <Form.Field
                control={form.control}
                name="password"
                render={() => (
                  <Form.Item className="mf-masked mf-listen">
                    <Form.Label aria-required>{t`Password`}</Form.Label>
                    <Form.Control>
                      <PasswordInput
                        {...form.register("password")}
                        placeholder={t`Enter your password`}
                      />
                    </Form.Control>
                    <Form.Message compact={false} />
                  </Form.Item>
                )}
              />

              <Form.Field
                control={form.control}
                name="passwordConfirmation"
                render={() => (
                  <Form.Item className="mf-masked mf-listen">
                    <Form.Label aria-required>{t`Confirm password`}</Form.Label>
                    <Form.Control>
                      <PasswordInput
                        {...form.register("passwordConfirmation")}
                        placeholder={t`Confirm your password`}
                      />
                    </Form.Control>
                    <Form.Message compact={false} />
                  </Form.Item>
                )}
              />
            </form>

            <PasswordTips />
          </section>

          <div className="mt-1 flex w-full flex-col">
            <Layout.Action type="submit" form={formId}>
              {t`Continue`}
            </Layout.Action>
          </div>
        </Form.Provider>
      </Layout.Content>
    </>
  );
};
